import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Pencil, Power, Trash2, UserPlus, Users } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { Member } from "@/api/generated/Member"
import type { Role } from "@/api/generated/Role"
import { useMembers, useRemoveMember, useUpdateMember } from "@/api/queries/members"
import { useSession } from "@/api/queries/session"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { StatusBadge, type StatusTone } from "@/components/app/status-badge"
import { UserAvatar } from "@/components/app/user-avatar"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { MemberSheet } from "@/features/members/member-sheet"
import { requireCap } from "@/lib/route-guards"

interface MembersSearch {
  invite?: boolean
}

export const Route = createFileRoute("/_app/settings/members")({
  beforeLoad: requireCap("orgAdmin"),
  staticData: { titleKey: "nav.items.members" },
  validateSearch: (s: Record<string, unknown>): MembersSearch => ({
    invite: s.invite === true || s.invite === "true" ? true : undefined,
  }),
  component: MembersPage,
})

const ROLE_TONE: Record<string, StatusTone> = { owner: "accent", admin: "info", member: "neutral" }

function MembersPage() {
  const { t } = useTranslation()
  const session = useSession()
  const tenantId = session.active_tenant?.id ?? 0
  const members = useMembers(tenantId)
  const navigate = useNavigate({ from: Route.fullPath })
  const search = Route.useSearch()
  const confirm = useConfirm()
  const remove = useRemoveMember(tenantId)
  const update = useUpdateMember(tenantId)
  const [editing, setEditing] = useState<Member | undefined>()
  const [sheetOpen, setSheetOpen] = useState(false)
  const [roleFilter, setRoleFilter] = useState<Role | "all">("all")

  const inviteOpen = !!search.invite
  const open = sheetOpen || inviteOpen
  const setOpen = (o: boolean) => {
    setSheetOpen(o)
    if (!o) {
      setEditing(undefined)
      if (inviteOpen) void navigate({ search: {}, replace: true })
    }
  }
  const openEdit = (m: Member) => {
    setEditing(m)
    setSheetOpen(true)
  }
  const openInvite = () => {
    setEditing(undefined)
    setSheetOpen(true)
  }

  const confirmRemove = (m: Member) =>
    confirm({
      title: t("members.removeConfirm.title", { name: m.display_name || m.username }),
      description: t("members.removeConfirm.description"),
      confirmLabel: t("members.remove"),
      destructive: true,
      onConfirm: async () => {
        await remove.mutateAsync(m)
        setOpen(false)
      },
    })

  const columns = useMemo(
    () =>
      createColumns<Member>((c) => [
        c.accessor((m) => `${m.display_name} ${m.username} ${m.email ?? ""}`, {
          id: "member",
          header: t("members.columns.member"),
          meta: { label: t("members.columns.member") },
          enableHiding: false,
          sortFn: "text",
          cell: ({ row }) => {
            const m = row.original
            return (
              <div className="flex min-w-0 items-center gap-2.5">
                <UserAvatar name={m.display_name || m.username} seed={m.username} size="sm" />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 truncate font-medium">
                    {m.display_name || m.username}
                    {m.user_id === session.user?.id && (
                      <span className="text-muted-foreground text-xs font-normal">{t("members.you")}</span>
                    )}
                  </div>
                  <div className="text-muted-foreground truncate text-xs">{m.email || `@${m.username}`}</div>
                </div>
              </div>
            )
          },
        }),
        c.accessor("role", {
          header: t("members.columns.role"),
          cell: ({ getValue }) => (
            <StatusBadge tone={ROLE_TONE[getValue()] ?? "neutral"}>
              {t(`roles.${getValue() as Role}`)}
            </StatusBadge>
          ),
        }),
        c.accessor(
          (m) => [m.perm_management, m.perm_chat, m.perm_workplace, m.perm_tasks].filter(Boolean).length,
          {
            id: "access",
            header: t("members.columns.access"),
            meta: { label: t("members.columns.access") },
            cell: ({ row }) => {
              const m = row.original
              const perms = [
                m.perm_management && t("perms.management"),
                m.perm_chat && t("perms.chat"),
                m.perm_workplace && t("perms.workplace"),
                m.perm_tasks && t("perms.tasks"),
              ].filter(Boolean) as string[]
              return perms.length ? (
                <div className="flex flex-wrap gap-1">
                  {perms.map((p) => (
                    <span
                      key={p}
                      className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[0.6875rem]"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground text-xs">{t("members.noAccess")}</span>
              )
            },
          },
        ),
        c.accessor("is_active", {
          header: t("members.columns.status"),
          cell: ({ row }) => (
            <div className="flex items-center gap-1">
              <StatusBadge tone={row.original.is_active ? "success" : "neutral"} dot>
                {row.original.is_active ? t("status.active") : t("status.inactive")}
              </StatusBadge>
              {row.original.is_platform_admin && (
                <StatusBadge tone="accent">{t("members.platformAdmin")}</StatusBadge>
              )}
            </div>
          ),
        }),
        c.accessor("last_login_at", {
          header: t("members.columns.lastActive"),
          sortFn: "datetime",
          sortUndefined: "last",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t, session.user?.id],
  )

  const rows = useMemo(
    () => (roleFilter === "all" ? members.data : members.data?.filter((m) => m.role === roleFilter)),
    [members.data, roleFilter],
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("members.title")}
        description={t("members.description", { name: session.active_tenant?.name ?? "" })}
        icon={Users}
        actions={
          <Button onClick={openInvite} data-testid="invite-member">
            <UserPlus />
            {t("members.invite")}
          </Button>
        }
      />
      <DataTable
        label={t("members.title")}
        data={rows}
        columns={columns}
        getRowId={(m) => String(m.id)}
        loading={members.isPending}
        error={members.error}
        onRetry={() => void members.refetch()}
        searchable
        searchPlaceholder={t("members.search")}
        columnToggle
        persistKey="members"
        initialSorting={[{ id: "member", desc: false }]}
        onRowClick={openEdit}
        isRowActive={(m) => open && editing?.id === m.id}
        toolbar={
          <Select value={roleFilter} onValueChange={(v) => setRoleFilter((v as Role | "all") ?? "all")}>
            <SelectTrigger size="sm" className="h-8 w-36" aria-label={t("members.columns.role")}>
              <SelectValue>
                {(v: string) => (v === "all" ? t("members.allRoles") : t(`roles.${v as Role}`))}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("members.allRoles")}</SelectItem>
              <SelectItem value="owner">{t("roles.owner")}</SelectItem>
              <SelectItem value="admin">{t("roles.admin")}</SelectItem>
              <SelectItem value="member">{t("roles.member")}</SelectItem>
            </SelectContent>
          </Select>
        }
        rowActions={(m) => {
          const self = m.user_id === session.user?.id
          return (
            <>
              <DropdownMenuItem onClick={() => openEdit(m)}>
                <Pencil />
                {t("common.edit")}
              </DropdownMenuItem>
              {!self && (
                <DropdownMenuItem onClick={() => update.mutate({ id: m.id, is_active: !m.is_active })}>
                  <Power />
                  {m.is_active ? t("members.deactivate") : t("members.activate")}
                </DropdownMenuItem>
              )}
              {!self && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={() => void confirmRemove(m)}>
                    <Trash2 />
                    {t("members.remove")}
                  </DropdownMenuItem>
                </>
              )}
            </>
          )
        }}
        emptyState={
          <EmptyState
            variant="plain"
            icon={Users}
            title={t("members.empty.title")}
            description={t("members.empty.description")}
            action={
              <Button size="sm" onClick={openInvite}>
                <UserPlus />
                {t("members.invite")}
              </Button>
            }
          />
        }
      />
      <MemberSheet
        open={open}
        onOpenChange={setOpen}
        tenantId={tenantId}
        member={editing}
        onRemove={(m) => void confirmRemove(m)}
      />
    </PageContainer>
  )
}
